/**
 * NewsWidget — Latest world headlines from curated RSS feeds.
 *
 * Source: `GET /aweni/api/v1/news?lang={user_locale}` (public endpoint,
 * server-side aggregation via NewsAggregator PHP service, 30 min cache).
 * Client-side caching via swr() — fresh 5 min, stale 24 h. Widget
 * never blocks; renders cached headlines instantly + revalidates in
 * background when a route brings home back into focus.
 *
 * Privacy: zero PII flows. The API endpoint receives `lang` only, no
 * coords/auth/user-id. The NewsAggregator returns the same payload
 * for everyone in the same language.
 *
 * Failure mode: if endpoint is offline or returns empty, the widget
 * silently hides itself rather than rendering an empty card. Per
 * AWENI/CLAUDE.md "Home Page Anti-Regression Rules" rule #10: one
 * broken widget never breaks the home.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface NewsItem {
  title: string;
  url: string;
  source: string;
  published_at: number;
}

interface NewsPayload {
  lang: string;
  items: NewsItem[];
  fetched_at: number;
  count: number;
}

const FRESH_TTL_MS = 5 * 60 * 1000; // 5 min — hot
const STALE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h — offline tolerance

async function fetchNews(lang: string, country: string | null): Promise<NewsPayload | null> {
  // country: ISO 3166-1 alpha-2 (e.g. "MA", "FR"). Server uses it to bias
  // the headline mix toward the user's country when feeds are available;
  // when omitted or unsupported, falls back to language-wide world news.
  const params = new URLSearchParams({ lang });
  if (country) params.set('country', country);
  const resp = await fetch(`/aweni/api/v1/news?${params.toString()}`, {
    cache: 'no-store',
  });
  if (!resp.ok) return null;
  const data = await resp.json() as NewsPayload;
  if (!Array.isArray(data?.items) || data.items.length === 0) return null;
  return data;
}

function formatRelativeTime(ts: number, locale: string, t: WidgetContext['t']): string {
  const seconds = Math.max(0, Math.floor((Date.now() / 1000) - ts));
  if (seconds < 60) return t('news_time_now') || 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  // Use Intl for richer formatting once it's >= 1 day
  try {
    const fmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    return fmt.format(-days, 'day');
  } catch {
    return `${days}d`;
  }
}

export const NewsWidget: Widget = {
  id: 'news',
  label: { en: 'Latest news', fr: 'Actualités', ar: 'آخر الأخبار', es: 'Últimas noticias', de: 'Neueste Nachrichten' },
  icon: '📰',
  defaultVisible: true,
  defaultOrder: 8,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const locale = ctx.locale || 'en';
    // Country bias from the shared LocationStore — when the user lives in
    // Morocco, surface MA-tagged feeds first; absent location, language-wide
    // mix. The cache key includes the country so changing city refreshes
    // the headlines instead of replaying the previous country's set.
    const country = ctx.getLocationService().countryCode || null;

    // Render skeleton immediately. Replaced when news arrives.
    container.innerHTML = `
      <div class="aweni-news-card" style="background:var(--card-bg);border:1px solid var(--border);border-radius:16px;padding:14px 16px;">
        <div class="aweni-news-header" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
          <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:var(--text-secondary);">
            📰 ${t('widget_news') || 'Latest news'}
          </span>
        </div>
        <div class="aweni-news-list" data-news-list>
          <div style="height:14px;background:var(--bg-secondary);border-radius:6px;margin-bottom:8px;" class="skeleton-pulse"></div>
          <div style="height:14px;width:80%;background:var(--bg-secondary);border-radius:6px;margin-bottom:8px;" class="skeleton-pulse"></div>
          <div style="height:14px;width:70%;background:var(--bg-secondary);border-radius:6px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const renderItems = (payload: NewsPayload): void => {
      const list = container.querySelector('[data-news-list]') as HTMLElement | null;
      if (!list) return;
      const top = payload.items.slice(0, 4);
      list.innerHTML = top.map((item) => `
        <a href="${item.url}" target="_blank" rel="noopener noreferrer"
           style="display:block;padding:8px 0;border-bottom:1px solid var(--border);text-decoration:none;color:inherit;">
          <div style="font-size:13px;font-weight:600;line-height:1.35;color:var(--text-primary);margin-bottom:2px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;">${escapeHtml(item.title)}</div>
          <div style="font-size:11px;color:var(--text-secondary);display:flex;gap:6px;align-items:center;">
            <span style="font-weight:600;">${escapeHtml(item.source)}</span>
            <span style="opacity:0.5;">·</span>
            <span>${formatRelativeTime(item.published_at, locale, t)}</span>
          </div>
        </a>
      `).join('');
      // Trim trailing border on last child
      const lastLink = list.querySelector('a:last-child') as HTMLElement | null;
      if (lastLink) lastLink.style.borderBottom = 'none';
    };

    const fail = (): void => {
      // Silent hide — empty news shouldn't show a broken card
      container.innerHTML = '';
    };

    // SWR: returns cached instantly if available, revalidates in background.
    swr<NewsPayload>(
      `news:${locale}:${country || 'world'}`,
      () => fetchNews(locale, country),
      {
        freshTtlMs: FRESH_TTL_MS,
        staleTtlMs: STALE_TTL_MS,
        onUpdate: (data) => { renderItems(data); },
      },
    ).then((data) => {
      if (data && data.items?.length) {
        renderItems(data);
      } else {
        fail();
      }
    }).catch(fail);
  },
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
